import { useState } from 'react';
import { DollarSign, Eye, MousePointer, Target, Globe, MapPin, Zap, TrendingUp, Activity, Award, ChevronDown, ChevronUp } from 'lucide-react';
import { WorldMap } from './WorldMap';
import { FeedbackState } from './FeedbackState';
import { fmt } from '../utils/helpers';

interface GeographicAnalysisProps {
    data: any;
    selectedCountry: any;
    setSelectedCountry: (country: any) => void;
    setCountryDetailModal: (country: any) => void;
}

export function GeographicAnalysis({
                                       data,
                                       selectedCountry,
                                       setSelectedCountry,
                                       setCountryDetailModal
                                   }: GeographicAnalysisProps) {
    const [geoViewMode, setGeoViewMode] = useState<'list' | 'map'>('list');
    const [mapMetric, setMapMetric] = useState<'revenue' | 'clicks' | 'impressions'>('revenue');

    const formatValue = (value: number, metric: string) => {
        switch (metric) {
            case 'revenue': return fmt.currency(value);
            case 'clicks': return fmt.number(value);
            case 'impressions': return fmt.number(value);
            default: return fmt.currency(value);
        }
    };

    const activeCountries = data.charts?.by_country?.filter((c: any) => c.impressions > 0) || [];

    if (activeCountries.length === 0) {
        return (
            <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                    <FeedbackState
                        type="empty"
                        title="Sem Dados Geográficos"
                        message="Não há dados de países para o período selecionado"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <Globe className="h-5 w-5 text-emerald-600" />
                            Distribuição Geográfica
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {activeCountries.length} países ativos • {fmt.currency(activeCountries.reduce((sum: number, c: any) => sum + c.revenue, 0))} total
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {/* Toggle de Visualização */}
                        <div className="flex gap-0.5 p-0.5 bg-white border border-gray-200 rounded-lg shadow-sm">
                            <button
                                onClick={() => setGeoViewMode('list')}
                                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${
                                    geoViewMode === 'list'
                                        ? 'bg-emerald-500 text-white shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                                Lista
                            </button>
                            <button
                                onClick={() => setGeoViewMode('map')}
                                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${
                                    geoViewMode === 'map'
                                        ? 'bg-emerald-500 text-white shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                <Globe className="h-3.5 w-3.5" />
                                Mapa
                            </button>
                        </div>

                        {/* Seletores de Métrica */}
                        <div className="flex gap-1 p-0.5 bg-white border border-gray-200 rounded-lg shadow-sm">
                            {[
                                { key: 'revenue', label: 'Receita', icon: DollarSign },
                                { key: 'impressions', label: 'Impressões', icon: Eye },
                                { key: 'clicks', label: 'Cliques', icon: MousePointer },
                            ].map((metric) => {
                                const Icon = metric.icon;
                                const isActive = mapMetric === metric.key;
                                return (
                                    <button
                                        key={metric.key}
                                        onClick={() => setMapMetric(metric.key as any)}
                                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                                            isActive
                                                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-sm'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                        }`}
                                    >
                                        <Icon className="h-3.5 w-3.5" />
                                        <span className="hidden sm:inline">{metric.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-6">
                {/* Visualização em Lista */}
                {geoViewMode === 'list' && (
                    <div className="space-y-3">
                        {activeCountries.slice(0, 10).map((country: any, index: number) => {
                            const maxValue = activeCountries[0][mapMetric];
                            const percentage = (country[mapMetric] / maxValue) * 100;
                            const isSelected = selectedCountry?.country === country.country;

                            // Calcular fill rate
                            const fillRate = country.impressions > 0
                                ? ((country.impressions / (country.impressions + country.unfilled_impressions)) * 100)
                                : 0;

                            return (
                                <div
                                    key={index}
                                    className={`relative rounded-xl border-2 transition-all cursor-pointer group ${
                                        isSelected
                                            ? 'border-emerald-400 bg-emerald-50 shadow-lg scale-[1.02]'
                                            : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow-lg hover:scale-[1.01]'
                                    }`}
                                    onClick={() => setSelectedCountry(isSelected ? null : country)}
                                >
                                    {/* Background Progress Bar */}
                                    <div
                                        className="absolute inset-0 bg-gradient-to-r from-emerald-100 via-emerald-50 to-transparent rounded-xl opacity-50 transition-all duration-500"
                                        style={{ width: `${percentage}%` }}
                                    />

                                    {/* Content */}
                                    <div className="relative p-5">
                                        <div className="flex items-start gap-4">
                                            {/* Ranking Badge */}
                                            <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-bold text-base transition-transform group-hover:scale-110 ${
                                                index === 0 ? 'bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-500 text-white shadow-xl' :
                                                    index === 1 ? 'bg-gradient-to-br from-slate-300 via-gray-300 to-slate-400 text-slate-700 shadow-lg' :
                                                        index === 2 ? 'bg-gradient-to-br from-orange-300 via-orange-400 to-orange-500 text-white shadow-lg' :
                                                            'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-600 shadow-md'
                                            }`}>
                                                {index < 3 && <Award className="h-5 w-5 absolute -top-1 -right-1 text-white drop-shadow" />}
                                                {index + 1}
                                            </div>

                                            {/* Country Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-3">
                                                        <h4 className="text-base font-bold text-gray-900">{country.country}</h4>
                                                        {country.pmr > 95 && (
                                                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full flex items-center gap-1">
                                                                <TrendingUp className="h-3 w-3" />
                                                                Alta PMR
                                                            </span>
                                                        )}
                                                        {country.ctr > 20 && (
                                                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full flex items-center gap-1">
                                                                <Activity className="h-3 w-3" />
                                                                Alto Engajamento
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-xl font-bold text-emerald-600">{formatValue(country[mapMetric], mapMetric)}</p>
                                                    </div>
                                                </div>

                                                {/* Métricas Principais */}
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                                                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-2.5">
                                                        <div className="flex items-center gap-1.5 mb-1">
                                                            <Eye className="h-3.5 w-3.5 text-blue-600" />
                                                            <span className="text-xs font-medium text-blue-900">Impressões</span>
                                                        </div>
                                                        <p className="text-sm font-bold text-blue-700">{fmt.number(country.impressions)}</p>
                                                    </div>

                                                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-2.5">
                                                        <div className="flex items-center gap-1.5 mb-1">
                                                            <MousePointer className="h-3.5 w-3.5 text-purple-600" />
                                                            <span className="text-xs font-medium text-purple-900">Cliques</span>
                                                        </div>
                                                        <p className="text-sm font-bold text-purple-700">{fmt.number(country.clicks)}</p>
                                                    </div>

                                                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-2.5">
                                                        <div className="flex items-center gap-1.5 mb-1">
                                                            <Target className="h-3.5 w-3.5 text-orange-600" />
                                                            <span className="text-xs font-medium text-orange-900">CTR</span>
                                                        </div>
                                                        <p className="text-sm font-bold text-orange-700">{fmt.percent(country.ctr)}</p>
                                                    </div>

                                                    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-2.5">
                                                        <div className="flex items-center gap-1.5 mb-1">
                                                            <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
                                                            <span className="text-xs font-medium text-emerald-900">eCPM</span>
                                                        </div>
                                                        <p className="text-sm font-bold text-emerald-700">{fmt.currency(country.ecpm)}</p>
                                                    </div>
                                                </div>

                                                {/* Indicador de Expansão */}
                                                <div className="flex items-center justify-between text-xs text-gray-500">
                                                    <div className="flex items-center gap-4">
                                                        <span className="flex items-center gap-1">
                                                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                                            Fill Rate: {fillRate.toFixed(1)}%
                                                        </span>
                                                        <span>
                                                            {country.domains?.filter((d: any) => d.impressions > 0).length || 0} domínios ativos
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-emerald-600 font-medium flex items-center gap-1 group-hover:text-emerald-700">
                                                            {isSelected ? (
                                                                <>
                                                                    <ChevronUp className="h-4 w-4" />
                                                                    Clique para fechar
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <ChevronDown className="h-4 w-4" />
                                                                    Clique para expandir
                                                                </>
                                                            )}
                                                        </span>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setCountryDetailModal(country);
                                                            }}
                                                            className="p-1.5 rounded-lg text-blue-600 hover:text-blue-700 hover:bg-blue-100 transition-colors"
                                                            title="Ver análise completa"
                                                        >
                                                            <Zap className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Expanded Details */}
                                        {isSelected && (
                                            <div className="mt-5 pt-5 border-t-2 border-emerald-200 animate-in slide-in-from-top duration-300">
                                                {/* Stats Detalhados */}
                                                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
                                                    <div className="bg-white rounded-lg p-3 border border-emerald-200 shadow-sm">
                                                        <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                                                            <DollarSign className="h-3 w-3" />
                                                            Receita Total
                                                        </p>
                                                        <p className="text-base font-bold text-emerald-600">{fmt.currency(country.revenue)}</p>
                                                    </div>
                                                    <div className="bg-white rounded-lg p-3 border border-blue-200 shadow-sm">
                                                        <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                                                            <Activity className="h-3 w-3" />
                                                            PMR
                                                        </p>
                                                        <p className="text-base font-bold text-blue-600">{fmt.percent(country.pmr)}</p>
                                                    </div>
                                                    <div className="bg-white rounded-lg p-3 border border-orange-200 shadow-sm">
                                                        <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                                                            <Target className="h-3 w-3" />
                                                            Não Preenchidas
                                                        </p>
                                                        <p className="text-base font-bold text-orange-600">{fmt.number(country.unfilled_impressions)}</p>
                                                    </div>
                                                    <div className="bg-white rounded-lg p-3 border border-purple-200 shadow-sm">
                                                        <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                                                            <Eye className="h-3 w-3" />
                                                            Req. Servidas
                                                        </p>
                                                        <p className="text-base font-bold text-purple-600">{fmt.number(country.requests_served)}</p>
                                                    </div>
                                                    <div className="bg-white rounded-lg p-3 border border-teal-200 shadow-sm">
                                                        <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                                                            <Activity className="h-3 w-3" />
                                                            Req. Elegíveis
                                                        </p>
                                                        <p className="text-base font-bold text-teal-600">{fmt.number(country.elegible_ad_request)}</p>
                                                    </div>
                                                </div>

                                                {/* Domínios e Ad Blocks */}
                                                <div className="grid lg:grid-cols-2 gap-4">
                                                    {/* Top Domínios */}
                                                    {country.domains && country.domains.filter((d: any) => d.impressions > 0).length > 0 && (
                                                        <div>
                                                            <h5 className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide flex items-center gap-2">
                                                                <Globe className="h-3.5 w-3.5 text-emerald-600" />
                                                                Top Domínios ({country.domains.filter((d: any) => d.impressions > 0).length})
                                                            </h5>
                                                            <div className="space-y-2">
                                                                {country.domains
                                                                    .filter((d: any) => d.impressions > 0)
                                                                    .slice(0, 3)
                                                                    .map((domain: any, idx: number) => (
                                                                        <div key={idx} className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                                                                            <div className="flex items-center justify-between mb-2">
                                                                                <p className="text-xs font-semibold text-gray-900 truncate flex-1 mr-2" title={domain.domain}>
                                                                                    {domain.domain}
                                                                                </p>
                                                                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
                                                                                    #{idx + 1}
                                                                                </span>
                                                                            </div>
                                                                            <div className="flex items-center justify-between text-xs">
                                                                                <span className="font-bold text-emerald-600">{fmt.currency(domain.revenue)}</span>
                                                                                <div className="flex gap-2">
                                                                                    <span className="text-gray-600">{fmt.number(domain.impressions)} imp</span>
                                                                                    <span className="text-blue-600 font-semibold">{fmt.percent(domain.ctr)} CTR</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Top Ad Blocks */}
                                                    {country.top_ad_blocks && country.top_ad_blocks.length > 0 && (
                                                        <div>
                                                            <h5 className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide flex items-center gap-2">
                                                                <Zap className="h-3.5 w-3.5 text-blue-600" />
                                                                Top Ad Blocks ({country.top_ad_blocks.length})
                                                            </h5>
                                                            <div className="space-y-2">
                                                                {country.top_ad_blocks.slice(0, 3).map((block: any, idx: number) => (
                                                                    <div key={idx} className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                                                                        <div className="flex items-center justify-between mb-2">
                                                                            <p className="text-xs font-semibold text-gray-900 truncate flex-1 mr-2" title={block.block}>
                                                                                {block.block.split('_')[1] || block.block}
                                                                            </p>
                                                                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
                                                                                #{idx + 1}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex items-center justify-between text-xs">
                                                                            <span className="font-bold text-blue-600">{fmt.currency(block.revenue)}</span>
                                                                            <div className="flex gap-2">
                                                                                <span className="text-gray-600">{fmt.number(block.impressions)} imp</span>
                                                                                <span className="text-purple-600 font-semibold">{fmt.percent(block.ctr)} CTR</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Visualização em Mapa */}
                {geoViewMode === 'map' && (
                    <div className="grid lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2">
                            <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-xl overflow-hidden border border-gray-200">
                                <WorldMap
                                    countries={activeCountries}
                                    selectedCountry={selectedCountry}
                                    onCountryClick={setSelectedCountry}
                                    mapMetric={mapMetric}
                                />
                            </div>
                        </div>

                        <div className="lg:col-span-1">
                            {selectedCountry ? (
                                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 border border-emerald-200 h-[500px] overflow-y-auto">
                                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-emerald-200">
                                        <div className="flex items-center gap-2">
                                            <MapPin className="h-4 w-4 text-emerald-600" />
                                            <h4 className="font-bold text-gray-900">{selectedCountry.country}</h4>
                                        </div>
                                        <button
                                            onClick={() => setSelectedCountry(null)}
                                            className="text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            ✕
                                        </button>
                                    </div>

                                    <div className="bg-white rounded-xl p-4 mb-3 shadow-sm border border-emerald-100">
                                        <p className="text-xs text-gray-600 mb-1">Receita Total</p>
                                        <p className="text-2xl font-bold text-emerald-600">{fmt.currency(selectedCountry.revenue)}</p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 mb-4">
                                        {[
                                            { label: 'Impressões', value: fmt.number(selectedCountry.impressions), color: 'blue' },
                                            { label: 'Cliques', value: fmt.number(selectedCountry.clicks), color: 'purple' },
                                            { label: 'CTR', value: fmt.percent(selectedCountry.ctr), color: 'orange' },
                                            { label: 'eCPM', value: fmt.currency(selectedCountry.ecpm), color: 'emerald' },
                                        ].map((metric, idx) => (
                                            <div key={idx} className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                                                <p className="text-xs text-gray-600 mb-0.5">{metric.label}</p>
                                                <p className={`text-sm font-bold text-${metric.color}-600`}>{metric.value}</p>
                                            </div>
                                        ))}
                                    </div>

                                    {selectedCountry.domains && selectedCountry.domains.filter((d: any) => d.impressions > 0).length > 0 && (
                                        <div className="mb-3">
                                            <h5 className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                                Domínios Ativos
                                            </h5>
                                            <div className="space-y-2">
                                                {selectedCountry.domains
                                                    .filter((d: any) => d.impressions > 0)
                                                    .slice(0, 3)
                                                    .map((domain: any, idx: number) => (
                                                        <div key={idx} className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                                                            <p className="text-xs font-semibold text-gray-900 mb-1 truncate" title={domain.domain}>
                                                                {domain.domain}
                                                            </p>
                                                            <div className="flex justify-between text-xs">
                                                                <span className="font-bold text-emerald-600">{fmt.currency(domain.revenue)}</span>
                                                                <span className="text-gray-600">{fmt.percent(domain.ctr)} CTR</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>
                                    )}

                                    {selectedCountry.top_ad_blocks && selectedCountry.top_ad_blocks.length > 0 && (
                                        <div>
                                            <h5 className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                                Top Ad Blocks
                                            </h5>
                                            <div className="space-y-2">
                                                {selectedCountry.top_ad_blocks.slice(0, 3).map((block: any, idx: number) => (
                                                    <div key={idx} className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                                                        <p className="text-xs font-semibold text-gray-900 mb-1 truncate" title={block.block}>
                                                            {block.block}
                                                        </p>
                                                        <div className="flex justify-between text-xs">
                                                            <span className="font-bold text-emerald-600">{fmt.currency(block.revenue)}</span>
                                                            <span className="text-gray-600">{fmt.percent(block.ctr)} CTR</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-gray-50 rounded-xl h-[500px] flex items-center justify-center border-2 border-dashed border-gray-300">
                                    <div className="text-center text-gray-400 p-8">
                                        <Globe className="h-12 w-12 mx-auto mb-3 opacity-40" />
                                        <p className="text-sm font-medium">Selecione um país</p>
                                        <p className="text-xs mt-1">Clique no mapa para ver detalhes</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}